import { Heading } from "@planningcenter/doxy-web"
import { CustomFields } from "source/myChurchCenter/Profile/CustomFields"
import { Date as DateFmt } from "source/shared/DatetimeFmt"
import { Icon } from "source/shared/components"
import { Link, useSearchParams } from "react-router-dom"
import { ChurchCenterCustomFieldApiRecordWithRelationships } from "../Profile/types"
import { RemoveFromHouseholdButton } from "../Profile/RemoveFromHouseholdButton"
import { useBuiltinFieldRules } from "source/shared/hooks/useBuiltinFieldRules"
import { useOrganizationGradeLabel } from "source/shared/hooks/useOrganizationGrades"
import { useFlipperFeatureEnabled } from "source/shared/flipperFeatures"

function typeFilter(type: string) {
  return (record: { type: string }) => record.type === type
}

export function Profile({
  isChild,
  canEditHouseholdMemberInfo,
  householdName,
  householdMemberRole,
  personId,
  records,
}: {
  isChild: boolean
  canEditHouseholdMemberInfo: boolean
  householdName: string | null
  householdMemberRole: string | null
  personId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  records: Array<{ type: string; attributes: Record<string, any>; id: string }>
}) {
  const getGradeLabel = useOrganizationGradeLabel()
  const {
    campus: campusEditingRules,
    membership_type: membershipEditingRules,
  } = useBuiltinFieldRules()
  const parentsGuardiansEnabled = useFlipperFeatureEnabled(
    "ROLLOUT_parents_and_guardians",
  )
  const [searchParams, _setSearchParams] = useSearchParams()
  const emails = records.filter(typeFilter("Email"))
  const phoneNumbers = records.filter(typeFilter("PhoneNumber"))
  const addresses = records.filter(typeFilter("Address"))

  const customFields = records.filter(
    typeFilter("ChurchCenterCustomField"),
  ) as ChurchCenterCustomFieldApiRecordWithRelationships[]

  const anniversary = records.find(typeFilter("Anniversary"))?.attributes
    ?.anniversary
  const formattedAnniversary = anniversary
    ? DateFmt({ start: anniversary, year: !anniversary.includes("1885-") })
    : null

  const birthdate = records.find(typeFilter("Birthdate"))?.attributes?.birthdate
  const formattedBirthdate = birthdate
    ? DateFmt({ start: birthdate, year: !birthdate.includes("1885-") })
    : "No Birthdate"

  const medicalNote =
    records.find(typeFilter("Medical_Notes"))?.attributes?.medical_notes ||
    "No notes"
  const school =
    records.find(typeFilter("SchoolOption"))?.attributes?.value || ""

  const maritalStatus =
    records.find(typeFilter("MaritalStatus"))?.attributes?.value || "No status"

  const gender = records.find(typeFilter("Gender"))?.attributes?.gender || ""

  const campus =
    records.find(typeFilter("Campus"))?.attributes?.name || "No primary campus"

  const membership =
    records.find(typeFilter("Membership"))?.attributes?.value || "No membership"

  const gradeKey = records.find(typeFilter("Grade"))?.attributes?.grade
  const graduationYear = records.find(typeFilter("Graduation_Year"))?.attributes
    ?.graduation_year

  const formattedGrade = getGradeLabel(gradeKey ?? null)

  function GradeAndSchool() {
    if (isChild || school || gradeKey !== null) {
      return (
        <div className="d-f@sm f_1 mb-1">
          <Grade />
          <School />
        </div>
      )
    } else return null
  }

  function Grade() {
    return (
      <div>
        <dt className="c-tint2 fs-4">
          {gradeKey === null && graduationYear ? (
            <>Graduation Year</>
          ) : (
            <>Grade</>
          )}
          <Icon symbol="general#lock" className="fs-2 pl-1" />
        </dt>
        <dd className="mb-1">
          {gradeKey === null && graduationYear
            ? graduationYear
            : formattedGrade}
        </dd>
      </div>
    )
  }

  function School() {
    return (
      <div>
        <dt className="c-tint2 fs-4">School</dt>
        <dd>{school}</dd>
      </div>
    )
  }

  const selectedProfileId = searchParams.get("profile")
  const currentHouseholdId = searchParams.get("household")
  const editLink = `edit?profile=${selectedProfileId}${currentHouseholdId ? `&household=${currentHouseholdId}` : ""}`

  return (
    <>
      <div className="d-f ai-c jc-sb mb-2">
        <Heading level={2} size={3} text="Personal information" />
        {canEditHouseholdMemberInfo && (
          <Link
            to={`${editLink}&section=personal-information`}
            className="minor-btn secondary-btn btn"
            style={{ zIndex: 0 }}
          >
            Edit
          </Link>
        )}
      </div>
      <dl className="action-drawer mb-4">
        <div className="d-f@sm f_1 mb-1">
          {campusEditingRules !== "hidden" && (
            <div>
              <dt className="c-tint2 fs-4">Campus</dt>
              <dd className="mb-1">{campus}</dd>
            </div>
          )}
          {membershipEditingRules !== "hidden" && (
            <div>
              <dt className="c-tint2 fs-4">Membership type</dt>
              <dd>{membership}</dd>
            </div>
          )}
        </div>
        <div className="d-f@sm f_1 mb-1">
          <div>
            <dt className="c-tint2 fs-4">Gender</dt>
            <dd className="mb-1">{gender}</dd>
          </div>
          <div>
            <dt className="c-tint2 fs-4">
              Birthdate
              {birthdate && (
                <Icon symbol="general#lock" className="fs-2 pl-1" />
              )}
            </dt>
            <dd>{formattedBirthdate}</dd>
          </div>
        </div>
        <GradeAndSchool />
        {!isChild && (
          <div className="d-f@sm f_1 mb-1">
            <div>
              <dt className="c-tint2 fs-4">Marital status</dt>
              <dd className="mb-1">{maritalStatus}</dd>
            </div>
            {maritalStatus === "Married" && (
              <div>
                <dt className="c-tint2 fs-4">Anniversary</dt>
                <dd>{formattedAnniversary}</dd>
              </div>
            )}
          </div>
        )}
        {parentsGuardiansEnabled && householdMemberRole && (
          <div className="d-f@sm f_1 mb-1">
            <div>
              <dt className="c-tint2 fs-4">{`${householdName} role`}</dt>
              <dd className="mb-1">{householdMemberRole}</dd>
            </div>
          </div>
        )}
        <dt className="c-tint2 fs-4">Medical notes</dt>
        <dd>{medicalNote}</dd>
      </dl>

      <div className="mb-4">
        <div className="d-f ai-c jc-sb mb-2">
          <Heading level={2} size={3} text="Contact information" />
          {canEditHouseholdMemberInfo && (
            <Link
              to={`${editLink}&section=contact-information`}
              className="minor-btn secondary-btn btn"
              style={{ zIndex: 0 }}
            >
              Edit
            </Link>
          )}
        </div>
        {emails.length > 0 && (
          <div className="action-drawer mb-1">
            <div className="d-f ai-c">
              <span className="c-tint3 p-r t-1p mr-1">
                <Icon symbol="general#outlined-envelope" />
              </span>
              <Heading level={3} size={4} text="Email" />
            </div>
            {emails.map((email) => (
              <dl className="d-f ai-c" key={email.id}>
                <div className="pt-1">
                  <div className="c-tint2 fs-4">
                    {email.attributes.location}
                  </div>
                  <div>
                    {email.attributes.address}{" "}
                    {email.attributes.primary && (
                      <span className="badge ml-1">Primary</span>
                    )}
                  </div>
                </div>
              </dl>
            ))}
          </div>
        )}
        {phoneNumbers.length > 0 && (
          <div className="action-drawer mb-1">
            <div className="d-f ai-c">
              <span className="c-tint3 p-r t-1p mr-1">
                <Icon symbol="general#mobile-phone" />
              </span>
              <Heading level={3} size={4} text="Phone" />
            </div>
            {phoneNumbers.map((phoneNumber) => (
              <div className="d-f ai-c" key={phoneNumber.id}>
                <div className="pt-1">
                  <div className="c-tint2 fs-4">
                    {phoneNumber.attributes.location}
                  </div>
                  <div>
                    {phoneNumber.attributes.number}{" "}
                    {phoneNumber.attributes.primary && (
                      <span className="badge ml-1">Primary</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {addresses.length > 0 && (
          <div className="action-drawer">
            <div className="d-f ai-c">
              <span className="c-tint3 p-r t-1p mr-1">
                <Icon symbol="general#outlined-location-pin" />
              </span>
              <Heading level={3} size={4} text="Address" />
            </div>
            {addresses.map((address) => (
              <div className="d-f ai-c" key={address.id}>
                <div className="pt-1">
                  <div className="c-tint2 fs-4">
                    {address.attributes.location}
                  </div>
                  <div>
                    {address.attributes.street}
                    <br />
                    {address.attributes.city}, {address.attributes.state}{" "}
                    {address.attributes.zip}
                    {address.attributes?.country_name && (
                      <>
                        <br />
                        {address.attributes.country_name}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {customFields.length > 0 && (
        <CustomFields
          records={customFields}
          canEdit={canEditHouseholdMemberInfo}
        />
      )}
      <RemoveFromHouseholdButton personId={personId} />
    </>
  )
}
